import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';

import type { GroupMember } from '../groups/groupsApi.ts';
import { usePrefersLight } from '../groups/useMemberColour.ts';
import { PhotoSourceSheet } from './PhotoSourceSheet.tsx';
import { AislePicker } from './AislePicker.tsx';
import { AISLE_LABELS, type Aisle } from './aisleLabels.ts';
import { revealEditorAsKeyboardOpens } from './revealItem.ts';
import type { Item } from './itemsApi.ts';

interface ItemEditorProps {
  item: Item;
  /** Everyone currently in the group — the only people an item may be assigned to. */
  members: GroupMember[];

  /**
   * Who ticked it, if it is ticked. Only for the colour of the tick: the card draws the
   * same filled circle the collapsed row does, because opening an item you have already
   * got must not make it look as though you had not.
   */
  checkedBy?: GroupMember | undefined;
  /**
   * Called only when something actually changed — see {@link ItemEditor}'s note on
   * committing. All three fields are always sent, because the PUT has no "leave it alone".
   */
  onSave: (changes: { name: string; note: string | null; assignedToUserId: string | null }) => void;
  onDelete: () => void;
  /** Ticks or unticks the item. The caller closes the editor; see the checkbox below. */
  onToggle: () => void;
  /** Collapses the editor back to a row. Not a cancel — edits are already committed. */
  onCancel: () => void;

  /** The photo the household has for this item's *saved* name, if any. */
  photo?: { id: string; width: number; height: number } | undefined;

  /** A thumbnail of {@link photo}, already fetched by the caller. */
  photoUrl?: string | undefined;

  /**
   * Applied immediately, not on collapse — see the note on the photo row below.
   * Receives the original file; compressing it is the caller's job, because it is slow
   * and the caller is where the mutation lives.
   */
  onAttachPhoto?: ((file: File) => void) | undefined;

  onRemovePhoto?: (() => void) | undefined;

  /** Opens the photograph full size — the same viewer the eye on the row opens. */
  onViewPhoto?: (() => void) | undefined;

  /**
   * What to say while a photo is being shrunk or sent, why it was refused, or that it is
   * waiting for a connection. `queued` is not a failure: the photo is safe, and saying so
   * is the difference between trusting the app in a dead spot and taking it again.
   */
  photoStatus?: { busy: boolean; error: string | null; queued?: boolean } | undefined;

  /** Where this item's name currently sits — the household's answer or the shipped table's. */
  aisle?: Aisle | undefined;

  /** Whether that answer is the household's, which decides if there is anything to undo. */
  aisleCorrected?: boolean | undefined;

  /**
   * Applied immediately, not on collapse — the same argument as the photo row. It is filed
   * against the *name*, and the name is what the field above can change.
   */
  onChangeAisle?: ((aisle: Aisle) => void) | undefined;

  /** Back to whatever the shipped table says. */
  onClearAisle?: (() => void) | undefined;
}

/**
 * One item, expanded in place: its name, its note, its photo, who it is for, and removing
 * it.
 *
 * Inline rather than a modal. A modal would take over the screen, need dismissing, and
 * lose your place in a long list — all of which is exactly wrong when you are holding a
 * phone in one hand next to a shelf. The row does not go away while it is being edited;
 * it keeps its id, its tick and its assignment stripe, and grows downwards.
 *
 * <b>There is no Save button, and no Cancel.</b> Every edit commits on blur and on
 * collapse, because a form that is one tap from a shopping list should not also ask to be
 * dismissed correctly — and a Save that people forget to press loses the rename silently,
 * which is worse than any request this saves. It is affordable because {@link commit}
 * diffs first: a blur that changed nothing sends nothing, so tabbing through the card
 * costs no request, no version bump and no broadcast to everyone else's phone. The offline
 * queue sees an auto-save as exactly the update a Save used to enqueue.
 *
 * Escape still reverts, which is what anyone who has used a form expects, and it is the
 * only path that discards.
 *
 * Delete lives here rather than as a swipe or a long-press: both are invisible, and a
 * swipe is very easy to trigger by accident while scrolling a list one-handed.
 *
 * Assignment lives here too rather than on the row. It is changed rarely — most items keep
 * whatever they were given when they were added — and a picker on every row would cost
 * width on the one screen that has none to spare.
 */
export function ItemEditor({
  item,
  members,
  checkedBy,
  onSave,
  onDelete,
  onToggle,
  onCancel,
  photo,
  photoUrl,
  onAttachPhoto,
  onRemovePhoto,
  onViewPhoto,
  photoStatus,
  aisle,
  aisleCorrected = false,
  onChangeAisle,
  onClearAisle,
}: ItemEditorProps) {
  // '' is the unassigned option. A member who has since left the group is not in the
  // list, so their id falls back to '' and the next commit releases the item — which is
  // the right outcome: nobody who could do it is holding it.
  const savedAssignment = members.some((member) => member.userId === item.assignedToUserId)
    ? (item.assignedToUserId ?? '')
    : '';

  const [name, setName] = useState(item.name);
  const [note, setNote] = useState(item.note ?? '');
  const [assignedTo, setAssignedTo] = useState(savedAssignment);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [choosingPhoto, setChoosingPhoto] = useState(false);
  const [choosingAisle, setChoosingAisle] = useState(false);

  const prefersLight = usePrefersLight();

  const nameRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  // Escape is the one way out that discards, so it has to be able to stop the commit that
  // unmounting would otherwise run.
  const cancelled = useRef(false);

  /** The last payload handed to {@link onSave}; see the note where it is set. */
  const sent = useRef<string | null>(null);

  const trimmedName = name.trim();
  const assignee = members.find((member) => member.userId === assignedTo);
  const assigneeColour = assignee
    ? prefersLight
      ? assignee.colourOnLight
      : assignee.colourHex
    : undefined;

  const keptOnSavedName = whatStaysWithTheName(Boolean(photo), Boolean(onChangeAisle && aisle));

  const checkedColour = checkedBy
    ? prefersLight
      ? checkedBy.colourOnLight
      : checkedBy.colourHex
    : undefined;

  /**
   * Sends the edit, if there is one.
   *
   * Diffing here rather than leaving it to the server is what makes committing on every
   * blur reasonable: an unchanged row would still be a request, a version bump and a
   * broadcast that redraws the item on everyone else's phone for nothing.
   *
   * An emptied name is not a delete and not a save — it comes back. The trash is how an
   * item goes, and it asks twice.
   */
  function commit() {
    if (cancelled.current) {
      return;
    }

    if (trimmedName.length === 0) {
      setName(item.name);
      return;
    }

    const trimmedNote = note.trim();

    const unchanged =
      trimmedName === item.name &&
      trimmedNote === (item.note ?? '') &&
      assignedTo === savedAssignment;

    if (unchanged) {
      return;
    }

    const changes = {
      name: trimmedName,
      note: trimmedNote.length > 0 ? trimmedNote : null,
      assignedToUserId: assignedTo === '' ? null : assignedTo,
    };

    // Every way out commits and then unmounts, and unmounting commits again — one frame
    // before the optimistic write has reached the `item` prop that the diff above is
    // against. Remembering what was sent is what stops the chevron being two requests.
    const signature = JSON.stringify(changes);

    if (signature === sent.current) {
      return;
    }

    sent.current = signature;
    onSave(changes);
  }

  // Held in a ref so unmounting can commit whatever was last typed. Opening a different
  // row closes this one without either field being blurred first, and that edit is just as
  // real as one ended by a tap on the chevron.
  const commitRef = useRef(commit);

  useEffect(() => {
    commitRef.current = commit;
  });

  useEffect(
    () => () => {
      commitRef.current();
    },
    [],
  );

  /**
   * Shared by both file inputs, so the reset below exists once rather than twice.
   *
   * Clearing the value is what lets the same file be chosen again: without it a second
   * pick of the same photo fires no change event at all, and the button looks broken.
   */
  function handleFileChosen(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (file) {
      onAttachPhoto?.(file);
    }

    event.target.value = '';
  }

  // Closed first, then clicked, and both synchronously inside the same gesture — iOS only
  // honours a programmatic click on a file input while it is still handling a real tap.
  function pickFrom(input: HTMLInputElement | null) {
    setChoosingPhoto(false);
    input?.click();
  }

  /** Round the members and then back to nobody. Unassigned is part of the cycle. */
  function cycleAssignment() {
    const index = members.findIndex((member) => member.userId === assignedTo);
    const next = members[index + 1];
    setAssignedTo(next ? next.userId : '');
  }

  // On a pointing device, focus the name and put the caret at the end: typing then appends
  // rather than replacing what is there, and it is where the focus that was on the pen has
  // to go now that the button has been replaced by this card.
  //
  // Not on a phone. The pen means "change something about this item", and most of the time
  // that something is the assignment, the note or the photo — not the name. Raising the
  // keyboard uninvited buys half the screen of furniture over the card the tap just opened,
  // and dismissing it is a second deliberate action before the thing you came for is even
  // reachable. Tapping the name field is one tap, and it is the tap that says so.
  //
  // The card still has to ask to be shown either way: this row has just become several
  // times its own height, so on anything but the top of the list the photo row and the
  // strip below it open up behind the add bar. Where the keyboard does arrive it also
  // covers them, which is why that call keeps measuring while the viewport settles.
  useEffect(() => {
    const input = nameRef.current;
    if (input && !window.matchMedia('(pointer: coarse)').matches) {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }

    revealEditorAsKeyboardOpens(item.id);
  }, [item.id]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    commit();
    onCancel();
  }

  return (
    <li
      // The same id the row carries, because this *is* the row while it is being edited:
      // one address for the item however it happens to be drawn, so scrolling to it does
      // not have to know which of the two is on screen.
      id={`item-${item.id}`}
      className="item-editor"
      // The stripe, drawn exactly as the collapsed row draws it, and following the dot as
      // it is tapped — the card is the best place to learn what the stripe on a row means.
      style={assigneeColour ? { borderInlineStartColor: assigneeColour } : undefined}
    >
      <form
        className="item-editor__form"
        onSubmit={handleSubmit}
        // Escape reverts and collapses. It is the only way out that discards, so it says so
        // to the commit that unmounting is about to run.
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            cancelled.current = true;
            onCancel();
          }
        }}
      >
        {/* Still tappable, and still the fastest thing on the card: someone who opened
            the editor to check the note and then put the thing in the trolley should not
            have to close it first. Committing before ticking is what keeps a rename that
            has not been blurred yet.

            First of the two things in the leading gutter, the dot being the other. */}
        <button
          type="button"
          className="item-editor__tick"
          aria-pressed={item.isChecked}
          aria-label={`Tick off ${item.name}`}
          onClick={() => {
            commit();
            onToggle();
            onCancel();
          }}
        >
          {/* Drawn exactly as the collapsed row draws it, filled in the colour of whoever
              ticked it. The card left it hollow at first, on the theory that an item being
              edited is the one in your hand — which is wrong the moment you open something
              already in the trolley and it looks unticked. */}
          <span
            className={item.isChecked ? 'item-row__tick item-row__tick--checked' : 'item-row__tick'}
            style={
              checkedColour ? { borderColor: checkedColour, backgroundColor: checkedColour } : undefined
            }
            aria-hidden="true"
          >
            {item.isChecked && (
              <svg viewBox="0 0 16 16" width="14" height="14" focusable="false">
                <path
                  d="M3 8.5l3.2 3.2L13 5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        </button>

        <input
          ref={nameRef}
          className="item-editor__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={commit}
          aria-label="Item name"
          maxLength={120}
          autoCorrect="off"
          spellCheck={false}
          required
        />

        {/* Where the pen was, doing the opposite of what the pen did. It commits on the
            way out, so it is a collapse rather than a save — there is nothing else it
            could mean by the time it is tapped.

            First of the two things in the trailing gutter, the trash being the other. */}
        <button
          type="button"
          className="item-editor__collapse"
          onClick={() => {
            commit();
            onCancel();
          }}
          aria-label={`Collapse ${item.name}`}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
            <path
              d="M6 15l6-6 6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <input
          className="item-editor__input item-editor__input--note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onBlur={commit}
          placeholder="Add a note — 2 pints, the green one…"
          aria-label="Note"
          maxLength={240}
          autoCorrect="off"
          spellCheck={false}
        />

        {/* The photo row.

            Applied on the tap rather than on collapse, like every other switch in the app.
            Three reasons: the photo belongs to the *name* and a rename is what the card
            above can change, so deferring would race the two; compressing and uploading is
            the slow part and hiding it behind a later commit would make it look lost; and
            it is a different endpoint and a different key space, so it cannot ride inside
            the update the way the assignment does.

            The cost is that leaving does not un-attach. That is mild — the thumbnail is
            visibly there, and Remove is the undo. */}
        {onAttachPhoto && (
          <div className={photoUrl ? 'item-editor__photo item-editor__photo--filled' : 'item-editor__photo'}>
            {/* The thumbnail opens the photograph, exactly as the eye on the row does —
                the two are the same picture at two sizes, and a 44px crop of a shelf is
                the thing you most want to tap when you cannot tell what it is. */}
            {photoUrl &&
              (onViewPhoto ? (
                <button
                  type="button"
                  className="item-editor__thumb-button"
                  onClick={onViewPhoto}
                  aria-label={`Photo of ${item.name}`}
                >
                  <img
                    className="item-editor__thumb"
                    src={photoUrl}
                    alt=""
                    width={photo?.width}
                    height={photo?.height}
                  />
                </button>
              ) : (
                <img
                  className="item-editor__thumb"
                  src={photoUrl}
                  alt={`Photo of ${item.name}`}
                  width={photo?.width}
                  height={photo?.height}
                />
              ))}

            {/* Two inputs, because on a modern phone one cannot do both jobs.

                Android 13+ routes an image-only `accept` to the system photo picker,
                which is a media picker with no camera entry at all — so a plain input can
                only ever reach the gallery. Adding `capture` fixes that and breaks the
                other half: it *replaces* the picker with the camera and takes the gallery
                away. There is no single control that offers both, so there are two.

                `environment` is the rear camera, which is the one pointed at a shelf.

                Hidden inputs behind real controls: a bare file input cannot be given a
                48px tap target and its native label is untranslatable. They have no
                accessible name either, hence the test ids — the sheet in front of them is
                what a person interacts with. */}
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              data-testid="photo-camera-input"
              onChange={handleFileChosen}
            />

            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              hidden
              data-testid="photo-gallery-input"
              onChange={handleFileChosen}
            />

            <button
              type="button"
              className="item-editor__photo-action"
              onClick={() => setChoosingPhoto(true)}
              disabled={photoStatus?.busy}
              aria-label={photo ? 'Replace photo' : 'Add photo'}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
                <path
                  d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.6l1.2-2h7.4l1.2 2h2.6A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-9z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="13" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
              </svg>
              {/* The word goes first on a narrow screen, before the line does — see the
                  container query on the class. Both controls carry an `aria-label`, so
                  nothing is lost when it goes: the name they are found by is the label,
                  not the text. */}
              <span className="item-editor__photo-label">{photo ? 'Replace' : 'Add photo'}</span>
            </button>

            {/* One tap, unlike removing the item: re-attaching is the undo, and the
                photograph is the household's rather than this row's. Its label says
                "photo" because the trash below removes the item and both are one tap. */}
            {photo && onRemovePhoto && !photoStatus?.busy && (
              <>
                <span className="item-editor__photo-divider" aria-hidden="true" />

                <button
                  type="button"
                  className="item-editor__photo-action item-editor__photo-action--quiet"
                  onClick={onRemovePhoto}
                  aria-label="Remove photo"
                >
                  <TrashIcon size={18} />
                  <span className="item-editor__photo-label">Remove</span>
                </button>
              </>
            )}

            {/* Names the binding rather than explaining it. Both the photo and the aisle
                are filed under the *saved* name, so when the field has been edited away
                from it this line is the only thing that says where they will still be.

                One line for both, because it is one fact about one name — and because two
                lines would be two lines of a card that is already four controls tall. It
                sits in the photo row rather than beside the aisle glyph for the same
                reason the glyph carries no words: the gutter is 32px wide. */}
            {keptOnSavedName && trimmedName !== item.name && (
              <span className="item-editor__photo-hint">
                {keptOnSavedName} &ldquo;{item.name}&rdquo;
              </span>
            )}

            {/* The busy state says itself rather than replacing a label: shrinking a
                photograph takes long enough that silence reads as a dead control and gets
                tapped again. */}
            {photoStatus?.busy && (
              <span className="item-editor__photo-hint">Shrinking the photo…</span>
            )}

            {photoStatus?.queued && (
              <span className="item-editor__photo-hint">
                Saved — it&rsquo;ll upload when you&rsquo;re back online.
              </span>
            )}

            {photoStatus?.error && (
              <span className="item-editor__photo-error" role="alert">
                {photoStatus.error}
              </span>
            )}
          </div>
        )}

        {/* The bottom of the leading gutter, in line with the aisle and the trash.

            A dot in the assignee's colour, which is the same colour as the stripe down the
            side of the card — the one place in the app where what the stripes mean can be
            learned by tapping something. It sits in the gutter rather than in the row of
            controls because the gutter is what the colour is *about*: the tick at the top
            of it and the stripe beside it are the item's two colours, and this is the one
            you can change.

            It cycles rather than opening a picker: handing an item over is a two-person
            decision made in a shop, and a sheet for it is a screenful of dialog for a field
            most items never change. There is no name beside it — the gutter is 32px wide
            and a name would not fit in it — so the whole of who this is for is carried by
            the button's accessible label, which names them in words. Do not let that label
            become "Assign", or the colour becomes the only carrier.

            Hidden when there is nobody to hand anything to. On a one-person list it can
            only ever say "me", and a control with one value is furniture. */}
        {members.length > 1 && (
          <button
            type="button"
            className="item-editor__assign"
            onClick={cycleAssignment}
            aria-label={`For ${assignee ? assignee.displayName : 'anyone'}. Change who it is for`}
          >
            <span
              className={
                assignee
                  ? 'item-editor__assign-dot'
                  : 'item-editor__assign-dot item-editor__assign-dot--empty'
              }
              style={assigneeColour ? { backgroundColor: assigneeColour } : undefined}
              aria-hidden="true"
            />
          </button>
        )}

        {/* The middle of the bottom row, between the assignment dot and the trash.

            The last row of the card was a dot at one edge and a bin at the other with the
            whole content column empty between them, which is exactly the width a third
            control wants and the only place on the card that costs no height at all. It was
            beside the note before that, in the leading gutter — this is the same glyph, one
            row down and centred.

            <b>A glyph with no words.</b> Unlike the assignment dot, which needs its colour
            to mean anything, this one gives up nothing by being silent: the row is sitting
            under a heading that says FRUIT & VEG, a few rows up the same screen, and the
            control is only ever offered when those headings are being drawn. The aisle is
            already visible; saying it again here was a whole line of a card whose other
            controls are a name and a note.

            Which aisle it is *is* still carried in words, in the button's accessible label,
            because a screen reader on this button has no heading above the row to lean on.
            Do not relabel it "Aisle" — that is the change that would leave the answer
            nowhere at all.

            Applied on the tap rather than on collapse, like the photo: it is filed against
            the *name*, the field above can change that name, and it is a different endpoint
            and key space so it cannot ride inside the update the way assignment does.

            Withdrawn while the delete is armed, because the armed button takes this very
            cell — see the note below on why it keeps its trailing edge. */}
        {onChangeAisle && aisle && !confirmingDelete && (
          <button
            type="button"
            className="item-editor__aisle"
            onClick={() => setChoosingAisle(true)}
            aria-label={`In ${AISLE_LABELS[aisle]}. Change which aisle it is in`}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
              <path
                d="M4 6h16M4 12h16M4 18h9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {/* The bottom of the trailing gutter, under the chevron and in line with the dot.

            Two taps to delete, but no dialog. Removing something someone else added is
            worth a moment's pause; a modal for it would not be.

            A quiet glyph until it is asked, then a filled red button with words in it: the
            change in weight is what says the first tap did something, and it is what makes
            the second one deliberate rather than a repeat of the first. The words do not
            fit in the gutter, so the armed button takes the content column with it and
            keeps its trailing edge — the one edge that has to stay put, because the thumb
            is already there. */}
        {confirmingDelete ? (
          <button
            type="button"
            className="button button--danger button--small item-editor__delete item-editor__delete--armed"
            onClick={onDelete}
          >
            Really remove?
          </button>
        ) : (
          <button
            type="button"
            className="item-editor__delete item-editor__delete--quiet"
            onClick={() => setConfirmingDelete(true)}
            aria-label={`Remove ${item.name}`}
          >
            <TrashIcon />
          </button>
        )}
      </form>

      {/* Outside the form: a dialog inside one is legal but its buttons are not this
          form's business, and the sheet is answering a tap rather than filling anything
          in. */}
      {choosingPhoto && (
        <PhotoSourceSheet
          onTakePhoto={() => pickFrom(cameraRef.current)}
          onChoosePhoto={() => pickFrom(galleryRef.current)}
          onClose={() => setChoosingPhoto(false)}
        />
      )}

      {/* Titled with the *saved* name rather than what is in the field, because that is
          the key the correction is filed under — the same thing the hint above says in
          words when the two have come apart. */}
      {choosingAisle && aisle && onChangeAisle && (
        <AislePicker
          name={item.name}
          current={aisle}
          corrected={aisleCorrected}
          onChoose={(chosen) => {
            setChoosingAisle(false);

            // Re-picking an aisle the household has already chosen is not a change, and
            // sending it would wake every other phone to redraw a list that has not moved
            // — the same rule the item mutations follow. Picking the one the *table*
            // chose is different: nobody has said it yet, and saying it pins the name
            // there against a future build of the table.
            if (chosen !== aisle || !aisleCorrected) {
              onChangeAisle(chosen);
            }
          }}
          onClear={() => {
            setChoosingAisle(false);
            onClearAisle?.();
          }}
          onClose={() => setChoosingAisle(false)}
        />
      )}
    </li>
  );
}

/**
 * Which of the name-keyed things this card holds would be left behind by a rename.
 *
 * Both are filed under the normalised name rather than against the row, so renaming moves
 * the item into a different chip history, a different remembered note, a different photo
 * and a different aisle — all at once, and all invisibly unless something says so.
 */
function whatStaysWithTheName(hasPhoto: boolean, hasAisle: boolean): string | null {
  // The verb comes along, because two of these are singular and one is not.
  if (hasPhoto && hasAisle) {
    return 'Photo and aisle stay with';
  }

  if (hasPhoto) {
    return 'Photo stays with';
  }

  return hasAisle ? 'Aisle stays with' : null;
}

/**
 * The first trash in the app: removing a photo, and removing the item.
 *
 * Smaller in the photo row than in the gutter, because the row has to hold two labelled
 * controls and a thumbnail on one line of a narrow phone.
 */
function TrashIcon({ size = 20 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
      <path
        d="M4 7h16M9.5 7V4.8h5V7M6.5 7l.9 12.2h9.2L17.5 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
